/** Authenticated client for the backend-owned billing and entitlement boundary. */
import { pendingSignupCode, redeemAmbassador, clearSignupCode } from './ambassadorApi';
import { localeRequestHeaders } from './localePreference';

const API_PREFIX = '/api/v1';

async function billingRequest(path, user, options = {}) {
  const token = await user.getIdToken();
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), path === '/checkout-session' ? 60000 : 20000);
  let response;
  try {
    response = await fetch(`${API_PREFIX}/billing${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...localeRequestHeaders(),
        ...(options.headers || {}),
      },
    });
  } catch {
    if (controller.signal.aborted) {
      throw new Error(path === '/checkout-session'
        ? 'Checkout is taking longer than expected. Please try again to check your existing checkout.'
        : 'Billing is taking longer than expected. Please try again.');
    }
    throw new Error('Unable to reach secure billing. Please try again.');
  } finally {
    window.clearTimeout(timeout);
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const requestError = new Error(error.detail || 'Billing request failed.');
    requestError.status = response.status;
    throw requestError;
  }
  return response.json();
}

export function getEntitlements(user) {
  return billingRequest('/entitlements', user, { method: 'GET' });
}

export async function getPaymentMethods(user) {
  return billingRequest('/payment-methods', user, { method: 'GET' });
}

export async function openProCheckout(user, provider = 'stripe') {
  // Apply a pending benefit after selection, while the checkout state is visible.
  // Listing payment methods should never wait for referral redemption.
  const code = pendingSignupCode(user);
  if (code) {
    await redeemAmbassador(user, code, 'Signup');
    clearSignupCode();
  }
  const body = { provider };
  const returnedPayment = new URLSearchParams(window.location.search);
  if (provider === 'whish' && returnedPayment.get('billing') === 'whish' && returnedPayment.get('whishAttempt') === 'failed') {
    // A UX hint only: the server verifies this payment before permitting a new one.
    body.failedPaymentId = returnedPayment.get('payment');
  }
  const response = await billingRequest('/checkout-session', user, { method: 'POST', body: JSON.stringify(body) });
  window.location.assign(response.url);
}

export function getWhishPayment(user, paymentId) {
  return billingRequest(`/whish/payments/${encodeURIComponent(paymentId)}`, user, { method: 'GET' });
}

export async function openBillingPortal(user) {
  const response = await billingRequest('/portal-session', user, { method: 'POST' });
  window.location.assign(response.url);
}

export function prepareBillingForAccountDeletion(user) {
  return billingRequest('/prepare-account-deletion', user, { method: 'POST' });
}
